<template>
  <div class="flow-branch-menu">
    <item-header type="flowBranch" :id="flowBranchId" @close="$emit('close')">
      <span v-if="ctx.flowBranch.branchType == 'sink'">⇒ </span>{{ ctx.element?.title }}<span v-if="ctx.flowBranch.branchType == 'source'"> ⇒</span>
    </item-header>

    <drawer-block :open="true" :title="$t('headings.flowBranchLimit')">
      <input-switch
        :value="ctx.flowBranch.limitsFlow"
        :label="$t('labels.limitsFlow')"
        :label-right="true"
        @update:value="ctx.updateKey('limitsFlow', $event)"
      />
      <p class="message-info">
        <template v-if="ctx.flowBranch.branchType == 'source'">
          {{
            ctx.flowBranch.limitsFlow
              ? $t("labels.sourceBranchFlowLimited")
              : $t("labels.sourceBranchFlowUnlimited")
          }}
        </template>
        <template v-else>
          {{
            ctx.flowBranch.limitsFlow
              ? $t("labels.sinkBranchFlowLimited")
              : $t("labels.sinkBranchFlowUnlimited")
          }}
        </template>
      </p>
    </drawer-block>

    <!-- INFLUENCE CURVE -->
    <drawer-block :title="$t('labels.strength')" :open="true">
      <div v-if="ctx.flowBranch.limitsFlow" class="flow-branch-menu__strength-simple mb-6">
        <input-number
          :value="branchStrength"
          :min="-Infinity"
          step="any"
          @update:value="branchStrength = $event"
        />
        * {{ $t("labels.flowValue") }} ({{ flowCalculatedValue.value }}{{ flowCalculatedValue.unit ? " " + flowCalculatedValue.unit : ""}})
      </div>

      <div class="column" style="position: relative;">
        <div class="flow-branch-menu__disabled" v-if="ctx.flowBranch.limitsFlow" :title="$t('texts.flowBranchCurveDisabled')"></div>
        <section>
          <div class="line-docanchor">
            <label class="label">{{ $t("labels.graph") }}</label>
            <documentation-anchor :object="ctx.flowBranch" prop="curve" />
          </div>
          <input-curve
            :value="Number(flowCalculatedValue.value)"
            :x-min="flowCalculatedValue.minValue"
            :x-max="flowCalculatedValue.maxValue"
            :point-list="ctx.flowBranch.curvePoints"
            :type="ctx.flowBranch.curveType"
            :formula="ctx.flowBranch.formula"
            class="mt-1"
            @change="onGraphChange"
            @update:formula="ctx.updateKey('formula', $event)"
            @hovervalue="updateCurveHover($event)"
          />
          <p>
            {{ $t("labels.xAxis") }}{{ $t(":") }}
            <i18n-t keypath="labels.valueOfX" tag="span" scope="global">
              <em>{{ flowTitle }}</em>
            </i18n-t>
            <br />
            {{ $t("labels.yAxis") }}{{ $t(":") }}
            <i18n-t keypath="labels.influenceOnX" tag="span" scope="global">
              <em>{{ ctx.element?.title || $t("labels.unnamed") }}</em>
            </i18n-t>
          </p>

          <i18n-t
            keypath="texts.influenceCurveConstantExample"
            tag="p"
            scope="global"
          >
            <template #startElement>
              <em>{{ flowTitle }}</em>
            </template>
            <template v-if="flowCalculatedValue.unit" #startUnit
              > {{ flowCalculatedValue.unit }}
            </template>
            <template #endElement>
              <em>{{ ctx.element?.title || $t("labels.unnamed") }}</em>
            </template>
            <template #startValue>{{ curveHover.x }}</template>
            <template #impulseValue>
              {{ $n(curveHover.y || 0, "displaySign") }}
            </template>
            <template v-if="ctx.element?.unit" #endUnit
              > {{ ctx.element?.unit }}
            </template>
          </i18n-t>
        </section>
      </div>
    </drawer-block>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";

import ItemHeader from "@editor/components/ItemHeader.vue";
import InputSwitch from "@core/input/InputSwitch.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputCurve from "../relation/InputCurve.vue";
import DrawerBlock from "@core/ui/DrawerBlock.vue";
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";

import { useFlowStore } from "./flows.store";
import { useFlowBranch } from "./useFlowBranch";
import { useFlowBranchStore } from "./flowBranches.store";

import type { GraphPoint } from "../relation/GraphPoint";
import type { CurveFormType } from "../relation/RelationItem";

const props = defineProps<{
  flowBranchId: number;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const flowStore = useFlowStore();
const flowBranchStore = useFlowBranchStore();
const ctx = useFlowBranch(() => props.flowBranchId);

const flowTitle = computed(() => flowStore.titles[ctx.flowBranch.flowId]);

function onGraphChange(data: { points: GraphPoint[]; type: CurveFormType }) {
  flowBranchStore.updateCurve({
    id: props.flowBranchId,
    curvePoints: data.points,
    curveType: data.type
  });
}

const flowCalculatedValue = computed(() => {
  if (!ctx.flow)
    return { minValue: 0, maxValue: 0, value: 0, unit: "", unitValid: true, valid: false };
  return flowStore.expressionValues[ctx.flow.uid];
});

const curveHover = reactive({
  x: 0,
  y: 0
});
function updateCurveHover(data: { x: number; y: number }) {
  curveHover.x = data.x;
  curveHover.y = data.y;
}

const branchStrength = computed({
  get: () => {
    const maxValue = flowCalculatedValue.value.maxValue;
    if (maxValue == null) return 1;
    return (
      (ctx.graphModel?.maxInfluence || 0) / maxValue
    );
  },
  set: (value: number) => {
    const formula = `${value}*x`;
    const newCurvePoints = ctx.getFormulaPoints(formula);
    ctx.update({
      formula,
      curvePoints: newCurvePoints,
      curveType: "formula" // formula is more precise (and faster) than linear
    });
  }
});
</script>

<style lang="scss">
.flow-branch-menu {
  position: relative;

  &__strength-simple {
    display: grid;
    grid-template-columns: 6rem 1fr;
    align-items: center;
    gap: 0.5rem;
  }

  &__disabled {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    background-color: rgba(255, 255, 255, 0.5);
  }
}
</style>
